/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * What a cover image should be, and when to say something about it.
 *
 * The same file is rendered into five different boxes, and NOT ONE of them sets
 * an aspect-ratio — every surface hard-crops to a fixed pixel height with
 * `object-fit: cover`:
 *
 *   detail hero   1128 x 250   (~4.5:1)  <- the tightest crop, and the widest render
 *   list card      415 x 180   (~2.3:1)
 *   home card      415 x 180   (~2.3:1)
 *   generic card   415 x 220   (~1.9:1)
 *   lightbox       up to 92vw, object-fit: CONTAIN — the whole image, uncropped
 *
 * 16:9 is the recommendation rather than the hero's own 4.5:1 precisely because
 * of that last row: a letterboxed 4.5:1 source survives the hero but looks like
 * a strip in the lightbox, which is the one place the full image is shown.
 *
 * 1920 wide serves the hero at ~85% of true 2x density. Exact would be 2256,
 * which is a materially bigger file for a difference nobody can see.
 */

export const RECOMMENDED_COVER = { width: 1920, height: 1080 };

/** Below this the detail hero, at 1128 CSS px, visibly softens. */
export const MIN_COVER_WIDTH = 1280;

export const TARGET_RATIO = 16 / 9;

/**
 * How far from 16:9 is still fine.
 *
 * Wide enough to accept 16:10 and 3:2 — both common straight out of a camera,
 * and both crop perfectly well — while still catching a square or a portrait
 * photo, which is where the hero's centre band throws away most of the picture.
 */
export const RATIO_TOLERANCE = 0.35;

export const COVER_HINT =
  'Recommended 1920x1080 (16:9), at least 1280 wide. Keep the subject centred: the event page '
  + 'crops the image to a wide band and lays the title across the bottom.';

/**
 * Advice, never a verdict. Returns null when there is nothing worth saying.
 *
 * Deliberately NOT wired into validateEventForm. A cover image is a judgement
 * call and the organizer can see the result; blocking a publish over one is the
 * kind of rule that gets worked around with a screenshot of the right size.
 */
export function checkCoverImage(dimensions) {
  if (!dimensions) return null;
  const { width, height } = dimensions;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;

  if (width < MIN_COVER_WIDTH) {
    return {
      level: 'warn',
      message: `This image is ${width}px wide. The event page shows it up to 1128px across, so `
        + `below ${MIN_COVER_WIDTH}px it will look soft — ${RECOMMENDED_COVER.width}x`
        + `${RECOMMENDED_COVER.height} is ideal. You can still use it.`,
    };
  }

  const ratio = width / height;
  if (Math.abs(ratio - TARGET_RATIO) > RATIO_TOLERANCE) {
    const shape = ratio < TARGET_RATIO ? 'tall' : 'wide';
    return {
      level: 'warn',
      message: `This image is ${width}x${height}, which is ${shape} for the space. The event page `
        + 'crops to a wide band, so expect some of it to be cut off. 16:9 crops cleanly.',
    };
  }

  return null;
}

/** Reads a picked file's pixel size. Resolves null when it cannot be read. */
export function readImageDimensions(file) {
  return new Promise((resolve) => {
    if (!file || typeof URL === 'undefined' || !URL.createObjectURL) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    // A file the browser cannot decode is not worth an error of its own: the
    // upload itself will reject it, with a better message than this could give.
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
}
